import React from 'react';
import { Shield, Clock, CreditCard, AlertCircle, Mail, Phone, CheckCircle, XCircle } from 'lucide-react';

const RefundPolicy = () => {
  const refundRules = [
    {
      icon: <XCircle className="w-6 h-6" />,
      title: 'No Refund After Trip Starts',
      description: 'Once the trip has started, cancellations will not be considered, and the full rental amount must be paid.',
      color: 'bg-red-100 text-red-600'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: '2 Hour Window',
      description: 'Cancellations made at least 2 hours before the trip start time will be eligible for a refund.',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: 'Last Minute Cancellations',
      description: 'No refunds will be issued for cancellations made less than 2 hours before the trip starts.',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: 'Processing Time',
      description: 'Refunds for eligible cancellations will be processed within 5-7 business days.',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: <XCircle className="w-6 h-6" />,
      title: 'No-Show Policy',
      description: 'If the bike is not picked up or delivered as per the booking, no refund will be provided.',
      color: 'bg-red-100 text-red-600'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Original Payment Method',
      description: 'Refunds will only be issued to the original payment method used during booking.',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: 'Terms Violation',
      description: 'If the customer violates the terms of use, the booking will be canceled without a refund.',
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Policy Modifications',
      description: 'The company reserves the right to modify the refund and cancellation policy without prior notice.',
      color: 'bg-gray-100 text-gray-600'
    }
  ];

  const refundTimeline = [
    {
      time: '72+ Hours Before',
      refund: '90% Refund',
      color: 'bg-green-500',
      note: '10% processing fee'
    },
    {
      time: '24-72 Hours Before',
      refund: '50% Refund',
      color: 'bg-yellow-500',
      note: 'Partial refund'
    },
    {
      time: '6-24 Hours Before',
      refund: '25% Refund',
      color: 'bg-orange-500',
      note: 'Limited refund'
    },
    {
      time: '2-6 Hours Before',
      refund: 'No Refund',
      color: 'bg-red-500',
      note: 'No refund available'
    },
    {
      time: 'After Trip Starts',
      refund: 'No Refund',
      color: 'bg-red-700',
      note: 'Full payment required'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <Shield className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Refund & Cancellation Policy
            </h1>
          </div>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Clear and transparent terms for cancellations and refunds at VE GO Rental Service
          </p>
        </div>
      </div>

      {/* Important Notice */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-6 shadow-md">
          <div className="flex items-start space-x-4">
            <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Important Information
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Please read this policy carefully before making a booking. By completing your reservation, you acknowledge and agree to these terms. For the best experience, we recommend booking in advance and canceling early if your plans change.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Refund Timeline */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
          Refund Timeline
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          Understanding when and how much you can get refunded based on cancellation timing
        </p>
        <div className="grid md:grid-cols-5 gap-4">
          {refundTimeline.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-300"
            >
              <div className={`${item.color} h-2`}></div>
              <div className="p-6">
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-500 mb-2">
                    {item.time}
                  </div>
                  <div className="text-xl font-bold text-gray-900 mb-2">
                    {item.refund}
                  </div>
                  <div className="text-xs text-gray-600">
                    {item.note}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Eligibility Rules */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Eligibility for Refunds
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Here are the detailed rules and conditions for refund eligibility
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {refundRules.map((rule, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className={`${rule.color} w-14 h-14 rounded-full flex items-center justify-center mb-4`}>
                  {rule.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {rule.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {rule.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How to Request a Refund */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          How to Request a Refund
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center border border-gray-100">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl font-bold text-blue-600">1</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Contact Support
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Reach out to our customer support team via email or phone with your booking details and cancellation request.
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8 text-center border border-gray-100">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl font-bold text-blue-600">2</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Verification
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Our team will verify your booking, check eligibility, and process your cancellation request accordingly.
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8 text-center border border-gray-100">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl font-bold text-blue-600">3</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Receive Refund
            </h3>
            <p className="text-gray-600 leading-relaxed">
              If eligible, your refund will be processed to your original payment method within 5-7 business days.
            </p>
          </div>
        </div>
      </div>

      {/* Key Points to Remember */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Key Points to Remember
          </h2>
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Advance Notice:</span> Cancel at least 2 hours before your scheduled pickup time to be eligible for a refund.
                </p>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Processing Time:</span> All eligible refunds are processed within 5-7 business days to the original payment method.
                </p>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">No-Show:</span> Failure to pick up the bike as scheduled results in no refund. Please inform us if you'll be late.
                </p>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Terms Compliance:</span> Violating our terms of service may result in booking cancellation without any refund.
                </p>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Policy Updates:</span> We reserve the right to modify this policy. Check our website for the latest version.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-2xl p-8 md:p-12 text-white">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Need Help with a Refund?</h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              If you have any questions or concerns about our Refund Policy or need assistance with a refund request, our customer support team is here to help.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <a
              href="tel:9921426002"
              className="bg-white text-blue-600 rounded-lg p-6 hover:bg-blue-50 transition-colors duration-200 flex items-center space-x-4 shadow-lg"
            >
              <div className="bg-blue-100 p-3 rounded-full">
                <Phone className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="text-sm text-gray-600 font-medium">Call Us</div>
                <div className="text-lg font-semibold">9921426002</div>
              </div>
            </a>
            <a
              href="mailto:vegobike00@gmail.com"
              className="bg-white text-blue-600 rounded-lg p-6 hover:bg-blue-50 transition-colors duration-200 flex items-center space-x-4 shadow-lg"
            >
              <div className="bg-blue-100 p-3 rounded-full">
                <Mail className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="text-sm text-gray-600 font-medium">Email Us</div>
                <div className="text-lg font-semibold break-all">vegobike00@gmail.com</div>
              </div>
            </a>
          </div>
          <div className="text-center mt-8">
            <p className="text-sm text-blue-200">
              Available Monday - Sunday, 9:00 AM - 8:00 PM IST
            </p>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-md p-8 border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="w-6 h-6 text-blue-600 mr-3" />
              Additional Information
            </h3>
            <div className="space-y-3 text-gray-700">
              <p>
                • Security deposits are always refundable if the vehicle is returned in good condition, regardless of trip cancellation.
              </p>
              <p>
                • For group bookings or long-term rentals, special cancellation terms may apply. Please contact us for details.
              </p>
              <p>
                • In case of force majeure events (natural disasters, government restrictions, etc.), special consideration may be given.
              </p>
              <p>
                • Partial cancellations (reducing rental duration) are treated as modifications and subject to availability.
              </p>
              <p>
                • This policy applies to all bookings made through our website, mobile app, or in-person at our office.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center text-sm text-gray-500">
          <p className="mb-2">Last Updated: October 2025</p>
          <p>
            By making a booking with VE GO Rental Service, you acknowledge that you have read, understood, and agree to this Refund & Cancellation Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
